// ============================================================================
// ehrApi.js — FHIR / EHR intake client wrappers (Build 0)
//
// Build 0 exposes ONLY capability discovery: a server-side GET {baseUrl}/metadata
// that returns a PHI-free CapabilityStatement summary. No PHI is read or written,
// and no tokens are involved. Later builds (Patient/Coverage candidate import)
// add their own wrappers here, mirroring the OCR/packet candidate flow.
// ============================================================================

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirebaseApp } from './store.firebase.js';

let functions = null;

const getFunctionsInstance = () => {
  if (!functions) {
    const app = getFirebaseApp();
    if (app) {
      functions = getFunctions(app);
    }
  }
  return functions;
};

// ============================================================================
// discoverEhrCapability
//
// Connects server-side to the configured FHIR server and returns a PHI-free
// CapabilityStatement summary (FHIR version, software, supported resources +
// interactions, advertised security). Read-only, zero PHI.
//
// @returns {Promise<{
//   ok: boolean,
//   provider: string,
//   metadataUrl: string,
//   httpStatus: number,
//   summary: {
//     resourceType: 'CapabilityStatement',
//     status: string|null,
//     fhirVersion: string|null,
//     publisher: string|null,
//     software: { name: string|null, version: string|null },
//     restModes: string[],
//     resourceCount: number,
//     supportedResources: Array<{ type: string, interactions: string[] }>,
//     security: { services: string[], cors: boolean|null, description: string|null },
//   },
// }>}
// ============================================================================

export const discoverEhrCapability = async () => {
  const fn = getFunctionsInstance();
  if (!fn) {
    throw new Error('Firebase Functions not initialized');
  }

  const callable = httpsCallable(fn, 'discoverEhrCapability');
  const result = await callable({});
  return result.data;
};

// ============================================================================
// importEhrPatientCoverageCandidates (Build 1A)
//
// Server-side reads sandbox Patient + Coverage and stores EHR candidates under
// the packet PHI subtree. Returns counts/status ONLY — no candidate values, no
// PHI. gp_employee only. No UI wired yet.
//
// @param {{ packetId: string, patientId: string }} params
// @returns {Promise<{
//   ok: boolean, status: 'COMPLETED'|'PARTIAL', importJobId: string,
//   candidateCount: number, patientCandidateCount: number,
//   coverageCandidateCount: number, coverageStatus: string|null,
// }>}
// ============================================================================

export const importEhrPatientCoverageCandidates = async ({ packetId, patientId }) => {
  const fn = getFunctionsInstance();
  if (!fn) {
    throw new Error('Firebase Functions not initialized');
  }

  const callable = httpsCallable(fn, 'importEhrPatientCoverageCandidates');
  const result = await callable({ packetId, patientId });
  return result.data;
};

// ============================================================================
// createPacketFromSmartLaunch (Build 2F)
//
// Authenticated gp_employee continuation of a SMART launch: creates (or
// idempotently re-selects) a never-PHI pre-referral packet container for the
// launch session. No Patient/Coverage import, no PHI, no Gate handoff yet.
// Returns safe metadata only (no patient/encounter/fhirUser/token/context IDs).
//
// @param {{ launchSessionId: string }} params
// @returns {Promise<{
//   ok: boolean, status: 'PACKET_CREATED'|'ALREADY_LINKED', packetId: string,
//   launchReadiness: 'PACKET_READY_FOR_IMPORT', ehrImportStatus: 'NOT_STARTED',
//   sourceSystemLabel: string|null,
// }>}
// ============================================================================

export const createPacketFromSmartLaunch = async ({ launchSessionId }) => {
  const fn = getFunctionsInstance();
  if (!fn) {
    throw new Error('Firebase Functions not initialized');
  }
  const callable = httpsCallable(fn, 'createPacketFromSmartLaunch');
  const result = await callable({ launchSessionId });
  return result.data;
};

// ============================================================================
// importSmartLaunchPatientCoverageCandidates (Build 2G)
//
// Authenticated gp_employee imports the launched Patient + Coverage into the
// 2F-linked packet as PHI candidates (server-side, single-use encrypted launch
// material). Returns safe rollup only — no patient/encounter/fhirUser/token/
// context IDs or candidate values.
//
// @param {{ launchSessionId: string }} params
// @returns {Promise<{
//   ok: boolean, status: 'COMPLETED'|'PARTIAL'|'FAILED', packetId: string,
//   importJobId: string, candidateCount: number, patientCandidateCount: number,
//   coverageCandidateCount: number, coverageStatus: string|null,
//   ehrImportStatus: string, launchReadiness: 'IMPORT_COMPLETE',
// }>}
// ============================================================================

export const importSmartLaunchPatientCoverageCandidates = async ({ launchSessionId }) => {
  const fn = getFunctionsInstance();
  if (!fn) {
    throw new Error('Firebase Functions not initialized');
  }
  const callable = httpsCallable(fn, 'importSmartLaunchPatientCoverageCandidates');
  const result = await callable({ launchSessionId });
  return result.data;
};

// ============================================================================
// getSmartLaunchContinuation (Build 2L)
//
// Read-only continuation summary for the authenticated gp_employee continuation
// panel. Returns a SAFE never-PHI projection only — no patient/encounter/token/
// fhirUser/fhirUserHash/candidate value. Drives the SMART Launch Continue panel's
// status + next-action; the create/import callables remain the mutation authority.
//
// @param {{ launchSessionId: string }} params
// @returns {Promise<{
//   ok: boolean, launchSessionId: string, launchReadiness: string|null,
//   contextStatus: string|null, patientContextPresent: boolean,
//   linkedPacketId: string|null, sourceSystemLabel: string|null,
//   canCreatePacket: boolean, canImport: boolean, nextAction: string,
// }>}
// ============================================================================

export const getSmartLaunchContinuation = async ({ launchSessionId }) => {
  const fn = getFunctionsInstance();
  if (!fn) {
    throw new Error('Firebase Functions not initialized');
  }
  const callable = httpsCallable(fn, 'getSmartLaunchContinuation');
  const result = await callable({ launchSessionId });
  return result.data;
};

// ============================================================================
// Build 1C — Gate EHR Suggestions: list + review EHR candidates
//
// Values are returned to gp_employee only (server-gated). No direct /phi/**
// client read. Mirrors the OCR review action set.
// ============================================================================

export const EHR_REVIEW_ACTION = Object.freeze({
  ACCEPT: 'ACCEPT',
  EDIT: 'EDIT',
  REJECT: 'REJECT',
});

/**
 * List active EHR candidates for a packet (gp-only; PHI-safe projection).
 * @param {{ packetId: string }} params
 * @returns {Promise<{ candidates: Array<object> }>}
 */
export const listPacketEhrCandidates = async ({ packetId }) => {
  const fn = getFunctionsInstance();
  if (!fn) {
    throw new Error('Firebase Functions not initialized');
  }
  const callable = httpsCallable(fn, 'listPacketEhrCandidates');
  const result = await callable({ packetId });
  return result.data;
};

/**
 * Review a single EHR candidate (ACCEPT / EDIT / REJECT). EDIT requires a
 * non-empty editedValue. Writes only candidate review fields server-side.
 * @param {{ packetId: string, candidateId: string, action: string, editedValue?: string, rejectedReason?: string }} params
 * @returns {Promise<{ success: boolean, status: string }>}
 */
export const reviewPacketEhrCandidate = async ({ packetId, candidateId, action, editedValue, rejectedReason }) => {
  const fn = getFunctionsInstance();
  if (!fn) {
    throw new Error('Firebase Functions not initialized');
  }
  const callable = httpsCallable(fn, 'reviewPacketEhrCandidate');
  const result = await callable({ packetId, candidateId, action, editedValue, rejectedReason });
  return result.data;
};

// ============================================================================
// SMART Build 2A — tenant EHR integration config (admin callables)
//
// Never-PHI, never-secret config. platform_admin writes/tests any tenant;
// tenant_admin lists own tenant (read-only). All via callables — no direct
// Firestore writes, no /phi/** access.
// ============================================================================

/**
 * @param {{ tenantId?: string }} params  (platform_admin passes tenantId; tenant_admin uses own)
 * @returns {Promise<{ connections: Array<object> }>}
 */
export const listTenantEhrConnections = async ({ tenantId } = {}) => {
  const fn = getFunctionsInstance();
  if (!fn) throw new Error('Firebase Functions not initialized');
  const callable = httpsCallable(fn, 'listTenantEhrConnections');
  const result = await callable({ tenantId });
  return result.data;
};

/**
 * @param {{ tenantId: string, connection: object }} params  (platform_admin only)
 * @returns {Promise<{ ok: boolean, connectionId: string, status: string }>}
 */
export const upsertTenantEhrConnection = async ({ tenantId, connection }) => {
  const fn = getFunctionsInstance();
  if (!fn) throw new Error('Firebase Functions not initialized');
  const callable = httpsCallable(fn, 'upsertTenantEhrConnection');
  const result = await callable({ tenantId, connection });
  return result.data;
};

/**
 * @param {{ tenantId: string, connectionId: string }} params  (platform_admin only)
 * @returns {Promise<{ ok: boolean, status: string }>}
 */
export const disableTenantEhrConnection = async ({ tenantId, connectionId }) => {
  const fn = getFunctionsInstance();
  if (!fn) throw new Error('Firebase Functions not initialized');
  const callable = httpsCallable(fn, 'disableTenantEhrConnection');
  const result = await callable({ tenantId, connectionId });
  return result.data;
};

/**
 * @param {{ tenantId: string, connectionId: string }} params  (platform_admin only)
 * @returns {Promise<{ ok: boolean, status: string, summary: object }>}
 */
export const testTenantEhrConnectionCapability = async ({ tenantId, connectionId }) => {
  const fn = getFunctionsInstance();
  if (!fn) throw new Error('Firebase Functions not initialized');
  const callable = httpsCallable(fn, 'testTenantEhrConnectionCapability');
  const result = await callable({ tenantId, connectionId });
  return result.data;
};

// ============================================================================
// SMART Build 2I — EHR user mapping admin callables
//
// tenant_admin (own tenant) + platform_admin (any tenant). Hash-only identity
// binding: responses carry a redacted fhirUserDisplay only — never the raw
// fhirUser, hash, PHI, or tokens.
// ============================================================================

/**
 * @param {{ tenantId?: string, connectionId: string }} params
 * @returns {Promise<{ mappings: Array<object> }>}
 */
export const listTenantEhrUserMappings = async ({ tenantId, connectionId } = {}) => {
  const fn = getFunctionsInstance();
  if (!fn) throw new Error('Firebase Functions not initialized');
  const callable = httpsCallable(fn, 'listTenantEhrUserMappings');
  const result = await callable({ tenantId, connectionId });
  return result.data;
};

/**
 * @param {{ tenantId?: string, connectionId: string, fhirUser: string, referralGateUid: string }} params
 * @returns {Promise<{ ok: boolean, mapping: object }>}
 */
export const upsertTenantEhrUserMapping = async ({ tenantId, connectionId, fhirUser, referralGateUid }) => {
  const fn = getFunctionsInstance();
  if (!fn) throw new Error('Firebase Functions not initialized');
  const callable = httpsCallable(fn, 'upsertTenantEhrUserMapping');
  const result = await callable({ tenantId, connectionId, fhirUser, referralGateUid });
  return result.data;
};

/**
 * @param {{ tenantId?: string, connectionId: string, mappingId: string }} params
 * @returns {Promise<{ ok: boolean, status: 'DISABLED' }>}
 */
export const disableTenantEhrUserMapping = async ({ tenantId, connectionId, mappingId }) => {
  const fn = getFunctionsInstance();
  if (!fn) throw new Error('Firebase Functions not initialized');
  const callable = httpsCallable(fn, 'disableTenantEhrUserMapping');
  const result = await callable({ tenantId, connectionId, mappingId });
  return result.data;
};

export default {
  discoverEhrCapability,
  importEhrPatientCoverageCandidates,
  createPacketFromSmartLaunch,
  importSmartLaunchPatientCoverageCandidates,
  getSmartLaunchContinuation,
  listPacketEhrCandidates,
  reviewPacketEhrCandidate,
  EHR_REVIEW_ACTION,
  listTenantEhrConnections,
  upsertTenantEhrConnection,
  disableTenantEhrConnection,
  testTenantEhrConnectionCapability,
  listTenantEhrUserMappings,
  upsertTenantEhrUserMapping,
  disableTenantEhrUserMapping,
};
